import React from "react";
import { FiCheck, FiCopy } from "react-icons/fi";
import { Button } from "newui/components/Button";
import { CheckboxWithLabel } from "newui/components/ui/CheckboxWithLabel";
import { WarningBox } from "newui/components/IconBox";

export interface RecoveryCodesStepProps {
	codes: string[];
	onDone: () => void;
}

const downloadCodesAsTxt = (codes: string[]) => {
	const blob = new Blob([codes.join("\n")], { type: "text/plain" });
	const url = URL.createObjectURL(blob);
	const link = document.createElement("a");
	link.href = url;
	link.download = "procuracon-recovery-codes.txt";
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	URL.revokeObjectURL(url);
};

export const RecoveryCodesStep = ({ codes, onDone }: RecoveryCodesStepProps) => {
	const [acknowledged, setAcknowledged] = React.useState(false);
	const [downloaded, setDownloaded] = React.useState(false);
	const [copied, setCopied] = React.useState(false);
	const [confirmingSkip, setConfirmingSkip] = React.useState(false);
	const skipWarningRef = React.useRef<HTMLDivElement>(null);

	// PRO-731 follow-up (item 2) — Modal's body already scrolls
	// (`scrollBehavior="inside"`), so this prompt was reachable, just not
	// visible: a browser never auto-scrolls a container to reveal content
	// that mounts below its current scroll position, so the confirm prompt
	// rendered below the fold whenever this step's content plus the prompt
	// together exceeded the modal's available body height — the user had to
	// realise something happened off-screen and scroll manually to find it.
	//
	// That total-content-vs-available-height budget is tighter in the enable
	// flow (`TwoFactorAuthModal`, size="md"/500px) than in the regenerate
	// flow (`RegenerateCodesModal`, default size="lg"/600px): this component
	// is unchanged between the two, but the WarningBox's prose wraps onto
	// more lines at the narrower width, so the enable flow needed more body
	// height for the exact same content and was the one to tip over a short
	// viewport (verified: 1440x760 overflowed here; the fix below removes the
	// dependency on any of that fitting by budget at all) — it's not that
	// regenerate is immune, just that it had more headroom to spare.
	// Scrolling the prompt into view on mount makes both flows correct
	// regardless of viewport height or width-driven text reflow.
	React.useEffect(() => {
		if (confirmingSkip) {
			skipWarningRef.current?.scrollIntoView({ block: "nearest" });
		}
	}, [confirmingSkip]);

	// Guard the browser-level close/refresh while the codes are still unsaved —
	// they cannot be retrieved again once the user leaves.
	React.useEffect(() => {
		if (downloaded) {
			return undefined;
		}
		const handleBeforeUnload = (e: BeforeUnloadEvent) => {
			e.preventDefault();
			e.returnValue = "";
		};
		window.addEventListener("beforeunload", handleBeforeUnload);
		return () => window.removeEventListener("beforeunload", handleBeforeUnload);
	}, [downloaded]);

	const handleDownload = () => {
		downloadCodesAsTxt(codes);
		setDownloaded(true);
		setConfirmingSkip(false);
	};

	// Copy is a valid way to save the codes — newline-separated, one per line.
	// Counts as "saved" so the leave-guard doesn't nag after a copy.
	const handleCopy = async () => {
		try {
			await navigator.clipboard.writeText(codes.join("\n"));
			setCopied(true);
			setDownloaded(true);
			setConfirmingSkip(false);
			window.setTimeout(() => setCopied(false), 2000);
		} catch {
			// Clipboard unavailable (permissions/insecure context) — fall back to
			// the download button, which remains the primary save path.
		}
	};

	// Warn if the user tries to finish without downloading — the codes are
	// unrecoverable once this step is left.
	const handleDone = () => {
		if (!downloaded) {
			setConfirmingSkip(true);
			return;
		}
		onDone();
	};

	return (
		<div className="flex flex-col gap-4">
			<WarningBox>
				<p className="font-roboto text-md text-gray-600">
					Save these recovery codes somewhere safe (e.g. password application). Each code can be used once to sign in if you
					lose access to your authenticator app.
				</p>
				<span className="font-bold">They will not be shown again and are not retrievable.</span>
			</WarningBox>
			<div className="relative">
				<button
					type="button"
					onClick={handleCopy}
					aria-label={copied ? "Recovery codes copied" : "Copy recovery codes"}
					title={copied ? "Copied" : "Copy codes"}
					className="absolute right-2 top-2 flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2 py-1 text-xs text-gray-600 hover:bg-gray-100"
					data-testid="copy-recovery-codes-btn"
				>
					{copied ? <FiCheck size={14} /> : <FiCopy size={14} />}
					{copied ? "Copied" : "Copy"}
				</button>
				<div
					className="grid grid-cols-2 gap-2 rounded-md border border-gray-200 bg-gray-50 p-4 pr-20 font-mono text-sm"
					data-testid="recovery-codes-list"
				>
					{codes.map((code) => (
						<span key={code}>{code}</span>
					))}
				</div>
			</div>
			<Button
				type="button"
				btntype="secondary"
				onClick={handleDownload}
				data-testid="download-recovery-codes-btn"
			>
				{downloaded ? "Download again (.txt)" : "Download codes (.txt)"}
			</Button>
			<CheckboxWithLabel
				name="recovery-codes-acknowledged"
				label="I have saved these recovery codes"
				checked={acknowledged}
				onChange={(e) => setAcknowledged(e.target.checked)}
			/>

			{confirmingSkip && (
				<div ref={skipWarningRef} data-testid="recovery-codes-skip-warning-container">
					<WarningBox>
						<p className="font-roboto text-md text-gray-700" data-testid="recovery-codes-skip-warning">
							You haven&apos;t downloaded your recovery codes yet. They cannot be shown or
							downloaded again. Are you sure you want to continue?
						</p>
						<div className="mt-3 flex gap-3">
							<Button
								type="button"
								btntype="secondary"
								onClick={handleDownload}
								data-testid="recovery-codes-download-instead-btn"
							>
								Download now
							</Button>
							<Button
								type="button"
								btntype="danger"
								onClick={onDone}
								data-testid="recovery-codes-continue-anyway-btn"
							>
								Continue without saving
							</Button>
						</div>
					</WarningBox>
				</div>
			)}

			{!confirmingSkip && <Button
				type="button"
				btntype="primary"
				disabled={!acknowledged}
				onClick={handleDone}
				data-testid="recovery-codes-done-btn"
			>
				Done
			</Button>}
		</div>
	);
};
